import { useEffect, useState, useRef, useCallback } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { api } from '../api/client';
import { route } from 'preact-router';
import { T } from '../styles/tokens';
import { Button, Divider } from '../components/ui';
import { loadStripe, type Stripe, type StripeElements } from '@stripe/stripe-js';

// Chave pública do Stripe (segura para expor no frontend)
const STRIPE_PK = 'pk_live_51T3CJYQUoH3HjIlrld774oyqCTv5bytKhLzARKxYkY9uI08WSORAWzAadhjY4ojthwdcGX4hcbKvbmBjHHP0VG0W00ZBiTJfoP';
const stripePromise = loadStripe(STRIPE_PK);

interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string;
  price_usd_monthly: number;
  price_usd_yearly: number;
  price_brl_monthly: number;
  price_brl_yearly: number;
  workspaces_limit: number;
  cpu_per_workspace: number;
  ram_mb_per_workspace: number;
  max_agents_per_workspace: number;
  max_skills: number;
  features: string;
  is_free: number;
  overage_allowed: number;
  white_label_enabled: number;
  custom_domain_enabled: number;
  custom_branding_enabled: number;
  custom_instances_enabled: number;
}

interface CurrentSub {
  plan_id: string;
  plan: string;
  status: string;
}

interface PlanInstanceConfig {
  plan_slug: string;
  memory_request: string;
  memory_limit: string;
  cpu_request: string;
  cpu_limit: string;
  auto_suspend_minutes: number;
  daily_hours_limit: number;
  max_replicas: number;
  storage_limit: string;
}

const PLAN_ORDER = ['free', 'starter', 'pro', 'business', 'enterprise'];

const FEATURES_TABLE: { label: string; key: string; type: 'number' | 'currency' | 'bool' | 'percent' | 'section' | 'instance' }[] = [
  { label: i18next.t('billing:feature_workspaces'), key: 'workspaces_limit', type: 'number' },
  { label: i18next.t('billing:feature_agents_per_ws'), key: 'max_agents_per_workspace', type: 'number' },
  { label: i18next.t('billing:feature_skills'), key: 'max_skills', type: 'number' },
  { label: 'CPU por Workspace', key: 'cpu_per_workspace', type: 'number' },
  { label: 'RAM por Workspace (MB)', key: 'ram_mb_per_workspace', type: 'number' },
  { label: i18next.t('billing:backup_weekly'), key: 'feature:backup_weekly', type: 'bool' },
  { label: i18next.t('billing:backup_daily'), key: 'feature:backup_daily', type: 'bool' },
  { label: i18next.t('billing:backup_hourly'), key: 'feature:backup_hourly', type: 'bool' },
  { label: i18next.t('billing:api_access'), key: 'feature:api_access', type: 'bool' },
  { label: i18next.t('billing:developer_hub'), key: 'feature:dev_hub', type: 'bool' },
  { label: i18next.t('billing:custom_llm'), key: 'feature:custom_llm_providers', type: 'bool' },
  { label: i18next.t('billing:terminal_access'), key: 'feature:terminal_access', type: 'bool' },
  { label: i18next.t('billing:instance_config'), key: 'feature:instance_config', type: 'bool' },
  { label: i18next.t('billing:orchestrator'), key: 'feature:orchestrator_access', type: 'bool' },
  { label: i18next.t('billing:gateway_token'), key: 'feature:orchestrator_gateway_token', type: 'bool' },
  { label: i18next.t('billing:priority_support'), key: 'feature:priority_support', type: 'bool' },
  { label: i18next.t('billing:advanced_analytics'), key: 'feature:advanced_analytics', type: 'bool' },
  { label: 'SSO', key: 'feature:sso', type: 'bool' },
  { label: i18next.t('billing:overage_allowed'), key: 'overage_allowed', type: 'bool' },
  { label: i18next.t('billing:white_label'), key: 'white_label_enabled', type: 'bool' },
  { label: i18next.t('billing:custom_domain'), key: 'custom_domain_enabled', type: 'bool' },
  { label: i18next.t('billing:custom_branding'), key: 'custom_branding_enabled', type: 'bool' },
  { label: i18next.t('billing:custom_instances'), key: 'custom_instances_enabled', type: 'bool' },
  { label: i18next.t('billing:dedicated_infra'), key: 'feature:dedicated_infra', type: 'bool' },
  { label: i18next.t('billing:instance_infra'), key: 'section:instance', type: 'section' },
  { label: 'RAM', key: 'instance:memory_limit', type: 'instance' },
  { label: 'CPU', key: 'instance:cpu_limit', type: 'instance' },
  { label: i18next.t('billing:storage'), key: 'instance:storage_limit', type: 'instance' },
  { label: i18next.t('billing:auto_suspend'), key: 'instance:auto_suspend_minutes', type: 'instance' },
  { label: i18next.t('billing:hours_per_day'), key: 'instance:daily_hours_limit', type: 'instance' },
  { label: i18next.t('billing:max_replicas'), key: 'instance:max_replicas', type: 'instance' },
];

// Icone de checkmark SVG
function Check() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
}
function Cross() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textDisabled} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
}

export function UpgradePage(_props: { path?: string }) {
  const { t } = useTranslation('billing');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentSub, setCurrentSub] = useState<CurrentSub | null>(null);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');
  const [exchangeRate, setExchangeRate] = useState<number>(5.5);
  const [planConfigs, setPlanConfigs] = useState<Record<string, PlanInstanceConfig>>({});

  useEffect(() => {
    Promise.all([
      api.get<{ plans: Plan[] }>('/subscription/plans').then(r => setPlans(r.plans || [])).catch(() => {}),
      api.getQuiet<{ subscription: CurrentSub }>('/subscription/me').then(r => setCurrentSub(r.subscription)).catch(() => setCurrentSub(null)),
      api.get<{ rate: number }>('/subscription/exchange-rate').then(r => { if (r.rate > 0) setExchangeRate(r.rate); }).catch(() => {}),
      api.get<any>('/cloud/plan-configs/all').then(r => {
        const configs: Record<string, PlanInstanceConfig> = {};
        const arr = r?.configs || r || [];
        (Array.isArray(arr) ? arr : []).forEach((c: any) => { configs[c.plan_slug] = c; });
        setPlanConfigs(configs);
      }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const sorted = [...plans].sort((a, b) => {
    const ai = PLAN_ORDER.indexOf(a.slug);
    const bi = PLAN_ORDER.indexOf(b.slug);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const formatMemory = (mem: string): string => {
    if (!mem) return '--';
    if (mem.endsWith('Gi')) return `${parseFloat(mem)} GB`;
    if (mem.endsWith('Mi')) { const mb = parseFloat(mem); return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`; }
    return mem;
  };
  const formatCpu = (cpu: string): string => {
    if (!cpu) return '--';
    if (cpu.endsWith('m')) return `${(parseFloat(cpu) / 1000).toFixed(1)} vCPU`;
    return `${parseFloat(cpu)} vCPU`;
  };
  const formatStorage = (storage: string): string => {
    if (!storage) return '--';
    if (storage.endsWith('Gi')) return `${parseFloat(storage)} GB`;
    if (storage.endsWith('Mi')) return `${parseFloat(storage)} MB`;
    return storage;
  };
  const formatAutoSuspend = (minutes: number): string => {
    if (!minutes || minutes === 0) return i18next.t('billing:always_on');
    return `${minutes} min`;
  };
  const formatDailyHours = (hours: number): string => {
    if (!hours || hours === 0) return i18next.t('billing:unlimited');
    return `${hours}h/dia`;
  };

  const currentIdx = currentSub ? PLAN_ORDER.indexOf(currentSub.plan?.toLowerCase() || '') : 0;
  const getPrice = (p: Plan) => billing === 'monthly' ? p.price_usd_monthly : p.price_usd_yearly;
  const getPriceBrl = (p: Plan) => {
    const usd = getPrice(p);
    if (billing === 'monthly' && p.price_brl_monthly > 0) return p.price_brl_monthly;
    if (billing === 'yearly' && p.price_brl_yearly > 0) return p.price_brl_yearly;
    return usd * exchangeRate;
  };
  const isCurrent = (p: Plan) => {
    if (!currentSub) return p.is_free === 1;
    return currentSub.plan?.toLowerCase() === p.slug;
  };
  const planIdx = (p: Plan) => PLAN_ORDER.indexOf(p.slug);
  const isLower = (p: Plan) => planIdx(p) < currentIdx;

  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [country, setCountry] = useState<string>('');
  const [modalPlan, setModalPlan] = useState<Plan | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [paymentGateways, setPaymentGateways] = useState<{ gateway: string; methods: string[] }[]>([]);

  // Estado do Stripe Payment Element (checkout transparente internacional)
  const [stripeModalPlan, setStripeModalPlan] = useState<Plan | null>(null);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState('');
  const [stripeSuccess, setStripeSuccess] = useState(false);
  const [stripeProcessing, setStripeProcessing] = useState(false);
  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const paymentElementMounted = useRef(false);
  const [payMethod, setPayMethod] = useState<'pix' | 'credit_card'>('pix');
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpMonth, setCardExpMonth] = useState('');
  const [cardExpYear, setCardExpYear] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [pixData, setPixData] = useState<{ orderId: string; qrCode: string; qrCodeText: string } | null>(null);
  const [pixPolling, setPixPolling] = useState(false);
  const [pixPollRef, setPixPollRef] = useState<ReturnType<typeof setInterval> | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [pixCopied, setPixCopied] = useState(false);

  useEffect(() => {
    api.get<{ country: string }>('/subscription/country').then(r => setCountry(r.country)).catch(() => setCountry('US'));
    // Buscar métodos de pagamento disponíveis pro país do usuário
    api.get<{ country: string; gateways: { gateway: string; methods: string[] }[]; methods: string[] }>('/checkout/payment-methods')
      .then(r => {
        setPaymentMethods(r.methods || []);
        setPaymentGateways(r.gateways || []);
        if (r.country) setCountry(r.country);
      })
      .catch(() => {});
  }, []);

  const handleCheckout = async (plan: Plan) => {
    if (plan.is_free || isCurrent(plan) || isLower(plan)) return;
    if (country === 'BR') {
      setModalPlan(plan); setPayMethod('pix'); setModalError(''); setPixData(null);
      setPaymentSuccess(false); setCardNumber(''); setCardName(''); setCardExpMonth(''); setCardExpYear(''); setCardCvv('');
      return;
    }

    // Checkout transparente via Stripe Payment Element (sem redirect)
    setStripeModalPlan(plan);
    setStripeError('');
    setStripeSuccess(false);
    setStripeClientSecret(null);
    setStripeLoading(true);
    paymentElementMounted.current = false;

    try {
      const res = await api.post<{ client_secret: string }>('/subscription/create-payment-intent', {
        plan_slug: plan.slug,
        billing_cycle: billing,
      });

      if (res.client_secret) {
        setStripeClientSecret(res.client_secret);

        // Montar Stripe Elements após receber client_secret
        const stripe = await stripePromise;
        if (stripe) {
          stripeRef.current = stripe;
          const elements = stripe.elements({
            clientSecret: res.client_secret,
            appearance: {
              theme: 'night',
              variables: {
                colorPrimary: '#7c3aed',
                colorBackground: '#1a1a2e',
                colorText: '#ffffff',
                colorDanger: '#ef4444',
                fontFamily: "'Manrope', system-ui, sans-serif",
                borderRadius: '8px',
              },
            },
          });
          elementsRef.current = elements;

          // Montar PaymentElement após renderizar o modal (retry até container existir)
          const mountPayment = (retries = 0) => {
            const container = document.getElementById('stripe-payment-element');
            if (container && !paymentElementMounted.current) {
              const paymentElement = elements.create('payment', {
                layout: 'tabs',
              });
              paymentElement.mount(container);
              paymentElementMounted.current = true;
            } else if (retries < 20) {
              setTimeout(() => mountPayment(retries + 1), 200);
            }
          };
          setTimeout(() => mountPayment(), 100);
        }
      }
    } catch (err: any) {
      setStripeError(err?.message || i18next.t('errors:payment_init_error'));
    } finally {
      setStripeLoading(false);
    }
  };

  // Confirmar pagamento no Stripe Payment Element
  const handleStripeConfirm = useCallback(async () => {
    if (!stripeRef.current || !elementsRef.current || stripeProcessing) return;
    setStripeProcessing(true);
    setStripeError('');

    try {
      const { error } = await stripeRef.current.confirmPayment({
        elements: elementsRef.current,
        confirmParams: {
          return_url: `${window.location.origin}/subscription/success?gateway=stripe`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setStripeError(error.message || i18next.t('errors:payment_error'));
      } else {
        // Pagamento confirmado sem redirect
        setStripeSuccess(true);
      }
    } catch (err: any) {
      setStripeError(err?.message || i18next.t('errors:payment_error'));
    } finally {
      setStripeProcessing(false);
    }
  }, [stripeProcessing]);

  const handleTransparentCheckout = async () => {
    if (!modalPlan) return;
    setModalLoading(true); setModalError('');
    try {
      const payload: any = { plan_slug: modalPlan.slug, billing_cycle: billing, transparent: true, paymentMethod: payMethod };
      if (payMethod === 'credit_card') {
        if (!cardNumber || !cardName || !cardExpMonth || !cardExpYear || !cardCvv) { setModalError(i18next.t('billing:fill_all_fields')); setModalLoading(false); return; }
        payload.cardData = { number: cardNumber.replace(/\s/g, ''), holder_name: cardName, exp_month: cardExpMonth, exp_year: cardExpYear, cvv: cardCvv };
      }
      const res = await api.post<any>('/subscription/checkout', payload, { timeout: 30000 });
      if (payMethod === 'pix' && res.qr_code) {
        setPixData({ orderId: res.orderId, qrCode: res.qr_code, qrCodeText: res.qr_code_text || '' });
        setPixPolling(true);
        const poll = setInterval(async () => {
          try { const s = await api.get<any>(`/subscription/checkout/${res.orderId}/status`); if (s.status === 'paid') { clearInterval(poll); setPixPollRef(null); setPixPolling(false); setPaymentSuccess(true); setPixData(null); } } catch {}
        }, 5000);
        setPixPollRef(poll);
        setTimeout(() => { clearInterval(poll); setPixPollRef(null); setPixPolling(false); }, 1800000);
      } else if (payMethod === 'credit_card') {
        if (res.status === 'paid') setPaymentSuccess(true);
        else if (res.status === 'failed') setModalError(i18next.t('billing:card_declined'));
        else setPaymentSuccess(true);
      }
    } catch (err: any) { setModalError(err?.message || i18next.t('errors:payment_error')); } finally { setModalLoading(false); }
  };

  // Loading skeleton
  if (loading) {
    return (
      <div style={{ padding: '60px 24px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ height: 28, width: 260, background: T.bgInput, borderRadius: T.radiusMd, margin: '0 auto 12px' }} />
          <div style={{ height: 18, width: 340, background: T.bgInput, borderRadius: T.radiusMd, margin: '0 auto' }} />
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{ flex: '1 1 200px', maxWidth: 240, height: 380, background: T.bgInput, borderRadius: T.radiusLg, border: `1px solid ${T.border}` }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '40px 24px 80px', maxWidth: 1200, margin: '0 auto', minHeight: '100vh',
      fontFamily: "'Manrope', system-ui, -apple-system, sans-serif",
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <h1 style={{ fontSize: T.text4xl, fontWeight: T.weightBold, color: T.text, margin: '0 0 8px', letterSpacing: '-0.5px' }}>
          {t('upgrade_title')}
        </h1>
        <p style={{ fontSize: T.textLg, color: T.textSecondary, margin: 0 }}>
          {t('upgrade_subtitle')}
        </p>
      </div>

      {/* Toggle mensal/anual */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 40 }}>
        <div style={{
          display: 'inline-flex', background: T.bgInput, borderRadius: T.radiusMd,
          border: `1px solid ${T.border}`, padding: 3,
        }}>
          {(['monthly', 'yearly'] as const).map(b => (
            <Button
              key={b}
              variant={billing === b ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setBilling(b)}
            >
              {b === 'monthly' ? t('monthly') : t('yearly')}
              {b === 'yearly' && <span style={{ fontSize: T.textXs, marginLeft: 4, opacity: 0.85 }}>-17%</span>}
            </Button>
          ))}
        </div>
      </div>

      {/* Cards dos planos */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(200px, 1fr))`,
        gap: 16, marginBottom: 56, maxWidth: 1100, margin: '0 auto 56px',
      }}>
        {sorted.map(p => {
          const current = isCurrent(p);
          const lower = isLower(p);
          const popular = p.slug === 'pro';
          const price = getPrice(p);
          const priceBrl = getPriceBrl(p);
          const feats = JSON.parse(p.features || '{}');

          return (
            <div
              key={p.id}
              style={{
                background: T.bgCard,
                border: popular ? `2px solid ${T.primary}` : current ? `2px solid ${T.success}` : `1px solid ${T.border}`,
                borderRadius: T.radiusLg, padding: '28px 22px', position: 'relative',
                opacity: lower ? 0.45 : 1,
                display: 'flex', flexDirection: 'column',
                transition: `border-color ${T.transitionNormal}`,
              }}
            >
              {/* Badge */}
              {current && (
                <div style={{
                  position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)',
                  background: T.success, color: T.text, fontSize: T.textXs, fontWeight: T.weightBold,
                  padding: '3px 14px', borderRadius: T.radiusFull, whiteSpace: 'nowrap',
                }}>Seu plano</div>
              )}
              {popular && !current && (
                <div style={{
                  position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)',
                  background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, color: T.text,
                  fontSize: T.textXs, fontWeight: T.weightBold,
                  padding: '3px 14px', borderRadius: T.radiusFull, whiteSpace: 'nowrap',
                }}>Recomendado</div>
              )}

              <h3 style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.text, margin: '6px 0 4px', textTransform: 'capitalize' }}>{p.name}</h3>
              <p style={{ fontSize: T.textSm, color: T.textMuted, margin: '0 0 16px', minHeight: 32 }}>{p.description}</p>

              {/* Preco */}
              <div style={{ marginBottom: 20 }}>
                <span style={{ fontSize: 32, fontWeight: 800, color: T.text }}>
                  ${price.toFixed(0)}
                </span>
                <span style={{ fontSize: T.textSm, color: T.textMuted }}>/{billing === 'monthly' ? 'mes' : 'ano'}</span>
                {price > 0 && (
                  <div style={{ fontSize: T.textXs, color: T.textMuted, marginTop: 2 }}>
                    ~ R${priceBrl.toFixed(0)}/{billing === 'monthly' ? 'mes' : 'ano'}
                  </div>
                )}
              </div>

              {/* Features com checkmarks */}
              <div style={{ fontSize: T.textSm, color: T.textSecondary, marginBottom: 20, flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Check />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Check />
                  <span>{p.workspaces_limit} workspace{p.workspaces_limit > 1 ? 's' : ''}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Check />
                  <span>{p.max_agents_per_workspace} agentes/workspace</span>
                </div>
                {feats.backup_hourly && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Check /><span>Backup por hora</span></div>}
                {feats.backup_daily && !feats.backup_hourly && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Check /><span>Backup diario</span></div>}
                {feats.backup_weekly && !feats.backup_daily && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Check /><span>Backup semanal</span></div>}
                {feats.api_access && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Check /><span>Acesso a API</span></div>}
                {feats.dev_hub && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Check /><span>Developer Hub</span></div>}
                {feats.custom_llm_providers && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Check /><span>Provedores LLM customizados</span></div>}
                {feats.terminal_access && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Check /><span>Acesso ao Terminal</span></div>}
                {feats.orchestrator_access && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Check /><span>Orquestrador</span></div>}
                {feats.priority_support && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Check /><span>Suporte prioritario</span></div>}
                {p.overage_allowed === 1 && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Check /><span>Excedente permitido</span></div>}
                {p.white_label_enabled === 1 && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Check /><span>White Label</span></div>}
              </div>

              {/* Botao CTA */}
              {current ? (
                <div style={{
                  textAlign: 'center', padding: '12px 0', borderRadius: T.radiusMd,
                  background: T.bgInput, color: T.textMuted, fontSize: T.textMd, fontWeight: T.weightSemibold,
                  border: `1px solid ${T.border}`,
                }}>Plano Atual</div>
              ) : lower ? (
                <div style={{
                  textAlign: 'center', padding: '12px 0', borderRadius: T.radiusMd,
                  background: T.bgInput, color: T.textDisabled, fontSize: T.textMd,
                  border: `1px solid ${T.border}`,
                }}>--</div>
              ) : (
                <Button
                  variant={popular ? 'primary' : 'secondary'}
                  size="md"
                  onClick={() => handleCheckout(p)}
                  disabled={checkoutLoading === p.slug}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  {checkoutLoading === p.slug ? t('common:processing') : t('do_upgrade')}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Tabela de comparacao */}
      <div style={{ marginBottom: 48, maxWidth: 1100, margin: '0 auto 48px' }}>
        <h2 style={{ fontSize: T.text2xl, fontWeight: T.weightBold, color: T.text, textAlign: 'center', marginBottom: 24 }}>
          Comparativo de Features
        </h2>
        <div style={{ overflowX: 'auto', background: T.bgCard, borderRadius: T.radiusLg, border: `1px solid ${T.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.textSm, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '28%' }} />
              {sorted.map(p => <col key={p.id} style={{ width: `${72 / sorted.length}%` }} />)}
            </colgroup>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '14px 16px', color: T.textMuted, borderBottom: `1px solid ${T.border}`, fontWeight: T.weightSemibold, fontSize: T.textXs, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Feature</th>
                {sorted.map(p => (
                  <th key={p.id} style={{
                    textAlign: 'center', padding: '14px 8px', borderBottom: `1px solid ${T.border}`,
                    fontWeight: T.weightSemibold, fontSize: T.textSm,
                    color: isCurrent(p) ? T.primary : T.text, textTransform: 'capitalize',
                  }}>{p.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURES_TABLE.map(feat => {
                if (feat.type === 'section') {
                  return (
                    <tr key={feat.key}>
                      <td colSpan={sorted.length + 1} style={{
                        padding: '12px 16px', background: T.primaryBg,
                        borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`,
                        color: T.primary, fontSize: T.textXs, fontWeight: T.weightBold,
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                      }}>{feat.label}</td>
                    </tr>
                  );
                }

                return (
                  <tr key={feat.key}>
                    <td style={{ padding: '10px 16px', color: T.textSecondary, borderBottom: `1px solid ${T.border}` }}>{feat.label}</td>
                    {sorted.map(p => {
                      let display: any;

                      if (feat.type === 'instance') {
                        const config = planConfigs[p.slug];
                        if (!config) { display = '--'; }
                        else {
                          const k = feat.key.replace('instance:', '');
                          if (k === 'memory_limit') display = formatMemory(config.memory_limit);
                          else if (k === 'cpu_limit') display = formatCpu(config.cpu_limit);
                          else if (k === 'storage_limit') display = formatStorage((config as any).storage_limit || (config as any).storage_size);
                          else if (k === 'auto_suspend_minutes') { const f2 = formatAutoSuspend(config.auto_suspend_minutes); display = f2 === 'Sempre ligada' ? <Check /> : f2; }
                          else if (k === 'daily_hours_limit') display = formatDailyHours(config.daily_hours_limit);
                          else if (k === 'max_replicas') display = String(config.max_replicas);
                          else display = '--';
                        }
                      } else if (feat.key.startsWith('feature:')) {
                        const fk = feat.key.replace('feature:', '');
                        const feats2 = JSON.parse(p.features || '{}');
                        display = feats2[fk] ? <Check /> : <Cross />;
                      } else {
                        const val = (p as any)[feat.key];
                        if (feat.type === 'bool') display = val === 1 ? <Check /> : <Cross />;
                        else if (feat.type === 'currency') display = `$${val}`;
                        else if (feat.type === 'percent') display = `${val}%`;
                        else display = String(val);
                      }

                      return (
                        <td key={p.id} style={{
                          textAlign: 'center', padding: '10px 8px', borderBottom: `1px solid ${T.border}`,
                          color: isCurrent(p) ? T.primary : T.text,
                          fontWeight: isCurrent(p) ? T.weightSemibold : T.weightNormal,
                        }}>{display}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer CTA */}
      <div style={{ textAlign: 'center', color: T.textMuted, fontSize: T.textMd }}>
        <p>Precisa de um plano personalizado? <a href="mailto:support@example.com" style={{ color: T.primary, textDecoration: 'none' }}>Fale conosco</a></p>
      </div>

      {/* Modal de checkout transparente (BR) */}
      {modalPlan && (
        <div
          onClick={() => {
            if (pixPollRef) { clearInterval(pixPollRef); setPixPollRef(null); setPixPolling(false); }
            setModalPlan(null);
          }}
          style={{
            position: 'fixed', inset: 0, background: T.bgOverlay, backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999999,
          }}
        >
          <div
            onClick={(e: any) => e.stopPropagation()}
            style={{
              background: T.bg, borderRadius: T.radiusXl, padding: 32, width: '100%', maxWidth: 440,
              maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${T.border}`,
              boxShadow: T.shadowXl,
            }}
          >
            {/* Header do modal */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.text, margin: 0 }}>Upgrade para {modalPlan.name}</h3>
              <Button variant="ghost" onClick={() => {
                if (pixPollRef) { clearInterval(pixPollRef); setPixPollRef(null); setPixPolling(false); }
                setModalPlan(null);
              }} style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: 18, cursor: 'pointer', padding: 4 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </Button>
            </div>

            {/* Info do preco */}
            <div style={{
              padding: '16px 18px', background: T.bgCard, borderRadius: T.radiusMd,
              marginBottom: 24, border: `1px solid ${T.border}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: T.textSm, color: T.textSecondary }}>{t('plan_info', { plan: modalPlan.name, cycle: billing === 'monthly' ? t('monthly') : t('yearly') })}</div>
                  <div style={{ fontSize: T.text2xl, fontWeight: 800, color: T.text, marginTop: 4 }}>
                    ${getPrice(modalPlan).toFixed(2)} <span style={{ fontSize: T.textSm, fontWeight: T.weightNormal, color: T.textMuted }}>USD</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.success }}>
                    R$ {getPriceBrl(modalPlan).toFixed(2)}
                  </div>
                  <div style={{ fontSize: T.textXs, color: T.textMuted }}>1 USD = R$ {exchangeRate.toFixed(2)}</div>
                </div>
              </div>
            </div>

            {paymentSuccess ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px', display: 'block' }}><circle cx="12" cy="12" r="10"/><polyline points="16 8 10 16 7 13"/></svg>
                <div style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.success, marginBottom: 8 }}>Pagamento confirmado!</div>
                <div style={{ fontSize: T.textMd, color: T.textSecondary, marginBottom: 20 }}>Seu plano foi atualizado para {modalPlan.name}</div>
                <Button variant="success" onClick={() => { setModalPlan(null); window.location.reload(); }}>Continuar</Button>
              </div>
            ) : pixData ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: T.textMd, fontWeight: T.weightSemibold, color: T.text, marginBottom: 4 }}>Pagamento via PIX</div>
                <div style={{ fontSize: T.textSm, color: T.textMuted, marginBottom: 16 }}>Escaneie o QR Code ou copie o codigo</div>
                <div style={{ display: 'inline-block', padding: 12, background: '#fff', borderRadius: T.radiusMd, marginBottom: 16 }}>
                  <img src={pixData.qrCode} alt="QR Code PIX" style={{ width: 200, height: 200, display: 'block' }} />
                </div>
                {pixData.qrCodeText && (
                  <div style={{ marginBottom: 16 }}>
                    <Button
                      variant={pixCopied ? 'success' : 'primary'}
                      size="sm"
                      onClick={async () => {
                        try { await navigator.clipboard.writeText(pixData!.qrCodeText); } catch {
                          const ta = document.createElement('textarea'); ta.value = pixData!.qrCodeText; ta.style.position = 'fixed'; ta.style.opacity = '0';
                          document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
                        }
                        setPixCopied(true); setTimeout(() => setPixCopied(false), 2000);
                      }}
                    >{pixCopied ? t('common:copied') : t('copy_pix')}</Button>
                  </div>
                )}
                {pixPolling && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.warning, display: 'inline-block' }} />
                    <span style={{ fontSize: T.textSm, color: T.warning }}>Aguardando pagamento...</span>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {/* Toggle métodos de pagamento (dinâmico via /payment-methods) */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                  {([
                    ...(paymentMethods.includes('pix') ? [{ id: 'pix' as const, label: t('pix_method'), desc: t('pix_desc'), icon: '⚡' }] : []),
                    ...(paymentMethods.includes('credit_card') || paymentMethods.includes('card') || paymentMethods.length === 0
                      ? [{ id: 'credit_card' as const, label: t('credit_card_method'), desc: t('credit_card_desc'), icon: '💳' }] : []),
                    ...(paymentMethods.includes('boleto') ? [{ id: 'pix' as const, label: t('boleto_method', { defaultValue: 'Boleto' }), desc: t('boleto_desc', { defaultValue: '1-3 dias úteis' }), icon: '📄' }] : []),
                  ]).map(m => (
                    <div key={m.id} onClick={() => { setPayMethod(m.id); setModalError(''); }} style={{
                      flex: 1, padding: '14px 16px', borderRadius: T.radiusMd, cursor: 'pointer',
                      background: payMethod === m.id ? T.primaryBg : T.bgCard,
                      border: `1px solid ${payMethod === m.id ? T.primary : T.border}`,
                      transition: `all ${T.transitionFast}`,
                    }}>
                      <div style={{ fontSize: T.textMd, fontWeight: T.weightSemibold, color: payMethod === m.id ? T.primary : T.textSecondary }}>{m.label}</div>
                      <div style={{ fontSize: T.textXs, color: T.textMuted, marginTop: 2 }}>{m.desc}</div>
                    </div>
                  ))}
                </div>

                {/* Formulario de cartao */}
                {payMethod === 'credit_card' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16, padding: 16, background: T.bgCard, borderRadius: T.radiusMd, border: `1px solid ${T.border}` }}>
                    <input placeholder={t("card_number")} value={cardNumber} inputMode="numeric" autoComplete="cc-number"
                      onInput={(e: any) => { const raw = e.target.value.replace(/\D/g, '').slice(0, 16); setCardNumber(raw.replace(/(\d{4})(?=\d)/g, '$1 ')); }}
                      style={{ width: '100%', height: 44, padding: '0 14px', background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: T.radiusMd, color: T.text, fontSize: T.textMd, fontFamily: T.fontMono, outline: 'none', letterSpacing: 2, boxSizing: 'border-box', transition: `border-color ${T.transitionFast}` } as any}
                      onFocus={(e: any) => { e.target.style.borderColor = T.borderFocus; }} onBlur={(e: any) => { e.target.style.borderColor = T.border; }}
                    />
                    <input placeholder="NOME NO CARTAO" value={cardName} autoComplete="cc-name"
                      onInput={(e: any) => setCardName(e.target.value.toUpperCase())}
                      style={{ width: '100%', height: 44, padding: '0 14px', background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: T.radiusMd, color: T.text, fontSize: T.textMd, outline: 'none', textTransform: 'uppercase', boxSizing: 'border-box', transition: `border-color ${T.transitionFast}` } as any}
                      onFocus={(e: any) => { e.target.style.borderColor = T.borderFocus; }} onBlur={(e: any) => { e.target.style.borderColor = T.border; }}
                    />
                    <div style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 2, display: 'flex', alignItems: 'center', background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: T.radiusMd, overflow: 'hidden' }}>
                        <input placeholder="MM" maxLength={2} value={cardExpMonth} inputMode="numeric" autoComplete="cc-exp-month"
                          onInput={(e: any) => setCardExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
                          style={{ flex: 1, height: 44, padding: '0 14px', background: 'transparent', border: 'none', color: T.text, fontSize: T.textMd, textAlign: 'center', outline: 'none', fontFamily: T.fontMono } as any}
                        />
                        <span style={{ color: T.border, fontSize: 18 }}>/</span>
                        <input placeholder="AA" maxLength={2} value={cardExpYear} inputMode="numeric" autoComplete="cc-exp-year"
                          onInput={(e: any) => setCardExpYear(e.target.value.replace(/\D/g, '').slice(0, 2))}
                          style={{ flex: 1, height: 44, padding: '0 14px', background: 'transparent', border: 'none', color: T.text, fontSize: T.textMd, textAlign: 'center', outline: 'none', fontFamily: T.fontMono } as any}
                        />
                      </div>
                      <input placeholder="CVV" maxLength={4} value={cardCvv} inputMode="numeric" autoComplete="cc-csc"
                        onInput={(e: any) => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        style={{ flex: 1, height: 44, padding: '0 14px', background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: T.radiusMd, color: T.text, fontSize: T.textMd, textAlign: 'center', outline: 'none', fontFamily: T.fontMono, boxSizing: 'border-box' } as any}
                        onFocus={(e: any) => { e.target.style.borderColor = T.borderFocus; }} onBlur={(e: any) => { e.target.style.borderColor = T.border; }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, padding: '8px 12px', background: T.successBg, borderRadius: T.radiusMd, border: `1px solid ${T.successBg}` }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                      <span style={{ fontSize: T.textXs, color: T.success }}>Pagamento seguro -- dados criptografados via Pagar.me</span>
                    </div>
                  </div>
                )}

                {modalError && <div style={{ color: T.danger, fontSize: T.textSm, marginBottom: 12 }}>{modalError}</div>}

                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleTransparentCheckout}
                  disabled={modalLoading}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  {modalLoading ? t('common:processing') : `${getPriceBrl(modalPlan).toFixed(2)}`}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de checkout Stripe Payment Element (internacional) */}
      {stripeModalPlan && (
        <div
          onClick={() => { setStripeModalPlan(null); setStripeClientSecret(null); elementsRef.current = null; paymentElementMounted.current = false; }}
          style={{
            position: 'fixed', inset: 0, background: T.bgOverlay, backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999999,
          }}
        >
          <div
            onClick={(e: any) => e.stopPropagation()}
            style={{
              background: T.bg, borderRadius: T.radiusXl, padding: 32, width: '100%', maxWidth: 480,
              maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${T.border}`,
              boxShadow: T.shadowXl,
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.text, margin: 0 }}>
                Upgrade para {stripeModalPlan.name}
              </h3>
              <button
                onClick={() => { setStripeModalPlan(null); setStripeClientSecret(null); elementsRef.current = null; paymentElementMounted.current = false; }}
                style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: 18, cursor: 'pointer', padding: 4 }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Info do preço */}
            <div style={{
              padding: '16px 18px', background: T.bgCard, borderRadius: T.radiusMd,
              marginBottom: 24, border: `1px solid ${T.border}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: T.textSm, color: T.textSecondary }}>
                    {t('plan_info', { plan: stripeModalPlan.name, cycle: billing === 'monthly' ? t('monthly') : t('yearly') })}
                  </div>
                  <div style={{ fontSize: T.text2xl, fontWeight: 800, color: T.text, marginTop: 4 }}>
                    ${getPrice(stripeModalPlan).toFixed(2)}
                    <span style={{ fontSize: T.textSm, fontWeight: T.weightNormal, color: T.textMuted }}> USD/{billing === 'monthly' ? 'mês' : 'ano'}</span>
                  </div>
                </div>
              </div>
            </div>

            {stripeSuccess ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px', display: 'block' }}>
                  <circle cx="12" cy="12" r="10"/><polyline points="16 8 10 16 7 13"/>
                </svg>
                <div style={{ fontSize: T.textXl, fontWeight: T.weightBold, color: T.success, marginBottom: 8 }}>Pagamento confirmado!</div>
                <div style={{ fontSize: T.textMd, color: T.textSecondary, marginBottom: 20 }}>Seu plano foi atualizado para {stripeModalPlan.name}</div>
                <Button variant="primary" onClick={() => { setStripeModalPlan(null); window.location.reload(); }}>Continuar</Button>
              </div>
            ) : stripeLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: T.textMd, color: T.textMuted }}>Preparando pagamento...</div>
              </div>
            ) : (
              <div>
                {/* Container do Stripe Payment Element */}
                <div id="stripe-payment-element" style={{ marginBottom: 20, minHeight: 200 }} />

                {stripeError && (
                  <div style={{ color: T.danger, fontSize: T.textSm, marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: T.radiusMd }}>
                    {stripeError}
                  </div>
                )}

                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleStripeConfirm}
                  disabled={stripeProcessing || !stripeClientSecret}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  {stripeProcessing ? t('common:processing') : t('subscribe_amount', { amount: getPrice(stripeModalPlan).toFixed(2) })}
                </Button>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, justifyContent: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round">
                    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                  <span style={{ fontSize: T.textXs, color: T.textMuted }}>Pagamento seguro via Stripe — criptografia end-to-end</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
